import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import JSZip from 'jszip';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import {
  EmptyState,
  ErrorState,
  LoadingSkeleton,
  PageHeader,
  StatusChip,
} from '../../components/primitives.jsx';
import {
  KpiHeroBand,
  StatusDonutMini,
  InlineScoreCell,
  bandTone,
  KpiBandSkeleton,
  BrandedEmptyState,
  StagePipeline,
  toast,
} from '../../components/index.js';
import { buildEnhancementReportPath } from '../briefings/reportEntryLinks.js';
import { ProvenanceBadge } from '../../components/ProvenanceBadge.jsx';
import {
  MISSING_VALUE_LABEL,
  entityTypeLabel,
  fieldLabel,
  humanizeEnum,
  sourceSystemLabel,
} from '../../lib/fieldLabels.js';
import './admin.css';
import './adminConfig.redesign.css';
import './ingestionCenter.css';

const ENHANCEMENT_REPORT_ENTRY_PATH = buildEnhancementReportPath();
const EMPTY_ITEMS = [];
// Plain-language placeholder for a genuinely absent value in row context.
const MISSING_CONTEXT_VALUE = MISSING_VALUE_LABEL;

function EditableTargetFieldCombobox({ column, value, options, onChange }) {
  const inputRef = useRef(null);
  const rootRef = useRef(null);
  const popupRef = useRef(null);
  const generatedId = useId().replace(/:/g, '');
  const listboxId = `ingestion-target-fields-${generatedId}`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [popupStyle, setPopupStyle] = useState(null);

  const filteredOptions = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (!needle) return options;
    return options.filter((field) => {
      const token = field.toLocaleLowerCase();
      const label = fieldLabel(field).toLocaleLowerCase();
      return token.includes(needle) || label.includes(needle);
    });
  }, [options, query]);

  const choices = useMemo(
    () => [
      { value: '', label: 'Ignore this column', ignored: true },
      ...filteredOptions.map((field) => ({
        value: field,
        label: fieldLabel(field),
        token: field,
      })),
    ],
    [filteredOptions],
  );

  function closeList() {
    setOpen(false);
    setQuery('');
    setActiveIndex(-1);
  }

  function openFullList() {
    const selectedIndex = options.indexOf(value);
    setQuery('');
    setActiveIndex(value === '' ? 0 : selectedIndex >= 0 ? selectedIndex + 1 : -1);
    setOpen(true);
  }

  function selectChoice(choice) {
    onChange(choice.value);
    closeList();
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }

  function handleKeyDown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        openFullList();
        return;
      }
      setActiveIndex((current) =>
        current < 0 ? (choices.length > 1 ? 1 : 0) : (current + 1) % choices.length,
      );
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        openFullList();
        return;
      }
      setActiveIndex((current) => (current <= 0 ? choices.length - 1 : current - 1));
      return;
    }
    if (event.key === 'Enter' && open && activeIndex >= 0) {
      event.preventDefault();
      selectChoice(choices[activeIndex]);
      return;
    }
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      closeList();
      return;
    }
    if (event.key === 'Tab') closeList();
  }

  useLayoutEffect(() => {
    if (!open) return undefined;

    function positionPopup() {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const edgeGap = 8;
      const popupGap = 5;
      const preferredWidth = Math.max(rect.width, 320);
      const width = Math.min(preferredWidth, window.innerWidth - edgeGap * 2);
      const left = Math.min(
        Math.max(edgeGap, rect.left),
        Math.max(edgeGap, window.innerWidth - width - edgeGap),
      );
      const estimatedHeight = Math.min(320, choices.length * 48 + 12);
      const roomBelow = window.innerHeight - rect.bottom - edgeGap;
      const roomAbove = rect.top - edgeGap;
      const placeBelow = roomBelow >= Math.min(estimatedHeight, 180) || roomBelow >= roomAbove;
      const maxHeight = Math.max(120, (placeBelow ? roomBelow : roomAbove) - popupGap);
      const top = placeBelow
        ? rect.bottom + popupGap
        : Math.max(edgeGap, rect.top - Math.min(estimatedHeight, maxHeight) - popupGap);
      setPopupStyle({ left, top, width, maxHeight });
    }

    positionPopup();
    window.addEventListener('resize', positionPopup);
    document.addEventListener('scroll', positionPopup, true);
    return () => {
      window.removeEventListener('resize', positionPopup);
      document.removeEventListener('scroll', positionPopup, true);
    };
  }, [choices.length, open]);

  useEffect(() => {
    if (!open) return undefined;
    function handleOutsidePointerDown(event) {
      if (rootRef.current?.contains(event.target) || popupRef.current?.contains(event.target)) return;
      closeList();
    }
    document.addEventListener('pointerdown', handleOutsidePointerDown);
    return () => document.removeEventListener('pointerdown', handleOutsidePointerDown);
  }, [open]);

  const activeOptionId =
    open && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined;
  const listbox = open
    ? createPortal(
        <div
          ref={popupRef}
          className="ingestion-remap-popup"
          style={popupStyle || undefined}
        >
          <div
            id={listboxId}
            className="ingestion-remap-listbox"
            role="listbox"
            aria-label={`Target field choices for ${column}`}
          >
            {choices.map((choice, index) => (
              <button
                key={choice.ignored ? 'ignore' : choice.value}
                id={`${listboxId}-option-${index}`}
                type="button"
                role="option"
                tabIndex={-1}
                aria-selected={choice.value === value}
                className={`ingestion-remap-option${index === activeIndex ? ' active' : ''}${
                  choice.value === value ? ' selected' : ''
                }`}
                onMouseDown={(event) => event.preventDefault()}
                onPointerMove={() => setActiveIndex(index)}
                onClick={() => selectChoice(choice)}
              >
                <span className="ingestion-remap-option-label">{choice.label}</span>
                {choice.token ? (
                  <span className="ingestion-remap-option-token mono">{choice.token}</span>
                ) : null}
                {choice.value === value ? (
                  <span className="ingestion-remap-option-check" aria-hidden="true">
                    ✓
                  </span>
                ) : null}
              </button>
            ))}
          </div>
          {filteredOptions.length === 0 && query.trim() ? (
            <div className="ingestion-remap-no-match" role="status">
              No matching suggestion. The typed canonical field will be kept.
            </div>
          ) : null}
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={rootRef} className="ingestion-remap-combobox">
      <input
        ref={inputRef}
        className="ingestion-remap-input"
        role="combobox"
        aria-label={`Target field for ${column}`}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={activeOptionId}
        value={value}
        placeholder="Ignore this column"
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        onClick={() => {
          if (!open) openFullList();
        }}
        onChange={(event) => {
          const nextValue = event.target.value;
          onChange(nextValue);
          setQuery(nextValue);
          setActiveIndex(-1);
          setOpen(true);
        }}
        onKeyDown={handleKeyDown}
      />
      <button
        type="button"
        className="ingestion-remap-toggle"
        aria-label={`Show target field choices for ${column}`}
        aria-expanded={open}
        tabIndex={-1}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => {
          if (open) closeList();
          else openFullList();
          inputRef.current?.focus();
        }}
      >
        <span aria-hidden="true">▾</span>
      </button>
      {listbox}
    </div>
  );
}

const FINDING_GROUPS = [
  {
    id: 'duplicates',
    title: 'Duplicate candidates',
    description:
      'Potential matches against existing governed records that need reviewer disposition.',
    match: (finding) => finding.code === 'duplicate_candidate',
  },
  {
    id: 'conflicts',
    title: 'In-file conflicts',
    description: 'Rows that collide with another staged row in the same import package.',
    match: (finding) => finding.code === 'in_file_duplicate',
  },
  {
    id: 'other',
    title: 'Other validation findings',
    description: 'Missing fields, data-quality blockers, and warnings that still need triage.',
    match: (finding) =>
      finding.code !== 'duplicate_candidate' && finding.code !== 'in_file_duplicate',
  },
];

function formatIngestionModeLabel(value) {
  switch (value) {
    case 'xlsx_upload':
      return 'Excel workbook';
    case 'source_extract':
      return 'Source-system extract';
    case 'csv_upload':
    default:
      return 'CSV upload';
  }
}

function formatContextValue(value) {
  if (value === null || value === undefined) return MISSING_CONTEXT_VALUE;
  if (typeof value === 'string' && value.trim().length === 0) return MISSING_CONTEXT_VALUE;
  return String(value);
}

// S1 — render a numeric financial value (before/after) with a $M suffix.
function formatDiffValue(value) {
  if (value === null || value === undefined) return '— (new)';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return `$${numeric.toFixed(1)}M`;
}

// S1 — render a signed delta with sign and tone; null for brand-new fields.
function formatDiffDelta(delta) {
  if (delta === null || delta === undefined) return null;
  const numeric = Number(delta);
  if (!Number.isFinite(numeric)) return null;
  const sign = numeric > 0 ? '+' : numeric < 0 ? '' : '';
  return `${sign}$${numeric.toFixed(1)}M`;
}

function diffDeltaTone(delta) {
  const numeric = Number(delta);
  if (!Number.isFinite(numeric) || numeric === 0) return 'flat';
  return numeric > 0 ? 'up' : 'down';
}

// A short, human field label for the enrichment diff table.
const DIFF_FIELD_LABELS = {
  obligatedUsdM: 'Obligated',
  plannedBudgetUsdM: 'Planned',
  expendedUsdM: 'Expended',
  forecastAtCompletionUsdM: 'Forecast at completion',
};

function diffFieldLabel(field) {
  return DIFF_FIELD_LABELS[field] || field;
}

// S8 — derive the Upload -> Map -> Validate -> Disposition -> Commit -> Lineage
// ribbon from the job's live status/validation/commitSummary. The active stage is
// where the job currently sits; a validation_failed job flags Disposition blocked;
// a committed job marches to done with the Lineage stage carrying the edge count.
export function deriveIngestionStages(job) {
  const status = String(job?.status || 'uploaded');
  const hasMapping = Array.isArray(job?.mappedRows) && job.mappedRows.length > 0;
  const hasValidation = Boolean(job?.validation);
  const errorRowCount = Number(job?.validation?.summary?.errorRowCount ?? job?.errorRowCount ?? 0);
  const skippedRowCount = Number(job?.validation?.summary?.skippedRowCount ?? 0);
  const isCommitted = status === 'committed';
  const readyToCommit = status === 'ready_to_commit';
  const validationFailed = status === 'validation_failed' || (hasValidation && errorRowCount > 0);
  const validatedClean = isCommitted || readyToCommit || (hasValidation && errorRowCount === 0);
  const lineageCount = Number(
    job?.commitSummary?.lineageRecordCount ?? job?.lineageRecordIds?.length ?? 0,
  );

  const validateStatus =
    validatedClean || validationFailed ? 'done' : hasMapping ? 'active' : 'upcoming';
  let dispositionStatus;
  if (validationFailed) dispositionStatus = 'blocked';
  else if (isCommitted || validatedClean) dispositionStatus = 'done';
  else dispositionStatus = 'upcoming';
  let commitStatus;
  if (isCommitted) commitStatus = 'done';
  else if (validatedClean && !validationFailed) commitStatus = 'active';
  else commitStatus = 'upcoming';

  return [
    { id: 'upload', label: 'Upload', status: 'done' },
    {
      id: 'map',
      label: 'Map',
      status: hasMapping ? 'done' : status === 'uploaded' ? 'active' : 'upcoming',
    },
    { id: 'validate', label: 'Validate', status: validateStatus },
    {
      id: 'disposition',
      label: 'Disposition',
      status: dispositionStatus,
      ...(skippedRowCount ? { count: skippedRowCount } : {}),
    },
    { id: 'commit', label: 'Commit', status: commitStatus },
    {
      id: 'lineage',
      label: 'Lineage',
      status: isCommitted ? 'done' : 'upcoming',
      ...(isCommitted && lineageCount ? { count: lineageCount } : {}),
    },
  ];
}

function buildRowContext(row, focusField = null) {
  if (!row || typeof row !== 'object') return EMPTY_ITEMS;
  const entries = Object.entries(row).filter(([key]) => !key.startsWith('_'));
  const prioritized = [];
  const seenKeys = new Set();
  if (focusField) {
    const focusEntry = entries.find(([key]) => key === focusField);
    if (focusEntry) {
      prioritized.push(focusEntry);
      seenKeys.add(focusEntry[0]);
    }
  }
  return [...prioritized, ...entries.filter(([key]) => !seenKeys.has(key))]
    .slice(0, 4)
    .map(([key, value]) => ({
      key,
      value: formatContextValue(value),
    }));
}

function getCommittedProjectFinanceTargets(job) {
  if (!job || job.targetEntityType !== 'financial_record' || !Array.isArray(job.mappedRows)) {
    return EMPTY_ITEMS;
  }
  const projectIds = new Set();
  job.mappedRows.forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const projectId = typeof row.scopeId === 'string' ? row.scopeId.trim() : '';
    if (projectId) projectIds.add(projectId);
  });
  return Array.from(projectIds).map((projectId) => ({
    projectId,
    href: `/projects/${encodeURIComponent(projectId)}?tab=finance`,
  }));
}

// Realistic three-system demo drops (F1). Each starter mirrors the shape of the
// real source-system export and carries exactly ONE deliberately fixable failure
// row so the per-row disposition beat is guaranteed to fire, then clears to green.
// Rows here are kept byte-identical to seed-data/ingestion.seed.json sampleUploads
// and to the shipped demo-assets/ingest files (asserted by a data-integrity test).
// FMIS obligated math: good rows (Q1-Q3) obligated sum to 27.5 (the prior canonical
// total for PRJ-9924-A); the fixed failure row (Q4, 3.9) lands the new obligated at
// 31.4 (delta +3.9) — the "watch it land" figure downstream waves rely on.
export const STARTER_IMPORTS = [
  {
    id: 'starter-fmis',
    label: 'FMIS / Tableau',
    ingestionMode: 'xlsx_upload',
    ingestionModeLabel: 'Excel workbook',
    detail:
      'FY2026 budget execution for Project Sentinel Guard by appropriation and quarter — planned, obligated, and expended, with a bad Q5/pending row to correct.',
    sourceSystem: 'CONN-PMRT-FINANCE',
    targetEntityType: 'financial_record',
    fileName: 'fmis-tableau-monthly-drop.xlsx',
    sourceSheetName: 'Budget Execution',
    rows: [
      {
        'Project Code': 'PRJ-9924-A',
        Appropriation: 'O&M',
        FY: '2026',
        Period: 'Q1',
        'Planned $M': '12.0',
        'Obligated $M': '9.4',
        'Expended $M': '6.2',
        'As Of Date': '2026-07-02',
      },
      {
        'Project Code': 'PRJ-9924-A',
        Appropriation: 'RDT&E',
        FY: '2026',
        Period: 'Q2',
        'Planned $M': '10.5',
        'Obligated $M': '9.1',
        'Expended $M': '5.8',
        'As Of Date': '2026-07-02',
      },
      {
        'Project Code': 'PRJ-9924-A',
        Appropriation: 'Procurement',
        FY: '2026',
        Period: 'Q3',
        'Planned $M': '11.2',
        'Obligated $M': '9.0',
        'Expended $M': '6.6',
        'As Of Date': '2026-07-02',
      },
      {
        'Project Code': 'PRJ-9924-A',
        Appropriation: 'O&M',
        FY: '2026',
        Period: 'Q5',
        'Planned $M': '4.5',
        'Obligated $M': 'pending',
        'Expended $M': '3.6',
        'As Of Date': '2026-07-03',
      },
    ],
  },
  {
    id: 'starter-servicenow',
    label: 'ServiceNow Request for Support',
    ingestionMode: 'source_extract',
    ingestionModeLabel: 'Source-system extract',
    detail:
      'ServiceNow RFS queue for virtual-care operations converted into governed OWHA actions — one ticket is missing its project link.',
    sourceSystem: 'CONN-SERVICENOW-CHANGE',
    targetEntityType: 'action',
    fileName: 'servicenow-rfs-export.csv',
    rows: [
      {
        number: 'RFS0012845',
        short_description: 'Provision telehealth carts for Sentinel Guard pilot sites',
        u_project: 'PRJ-9924-A',
        assignment_group: 'OWHA Virtual Care Ops',
        priority: 'high',
        sys_updated_on: '2026-07-01 09:14:32',
      },
      {
        number: 'RFS0012846',
        short_description: 'Reset MFA for virtual care clinician onboarding',
        u_project: 'PRJ-9924-A',
        assignment_group: 'OWHA Access Management',
        priority: 'moderate',
        sys_updated_on: '2026-07-01 11:02:10',
      },
      {
        number: 'RFS0012847',
        short_description: 'Bandwidth upgrade for tele-behavioral health nodes',
        u_project: 'PRJ-9924-A',
        assignment_group: 'Network Operations',
        priority: 'high',
        sys_updated_on: '2026-07-02 08:20:45',
      },
      {
        number: 'RFS0012848',
        short_description: 'Escalate scheduling outage for virtual care intake',
        u_project: '',
        assignment_group: 'OWHA Virtual Care Ops',
        priority: 'critical',
        sys_updated_on: '2026-07-02 14:37:00',
      },
      {
        number: 'RFS0012849',
        short_description: 'Retire duplicate telehealth support queue',
        u_project: 'PRJ-9924-A',
        assignment_group: 'Service Desk',
        priority: 'low',
        sys_updated_on: '2026-07-03 07:55:19',
      },
    ],
  },
  {
    id: 'starter-jira',
    label: 'Jira / GitLab Delivery',
    ingestionMode: 'csv_upload',
    ingestionModeLabel: 'CSV upload',
    detail:
      'Jira sprint export for the OWHA virtual-care board mapped to delivery work items — one issue is missing its summary.',
    sourceSystem: 'CONN-JIRA-AGILE',
    targetEntityType: 'work_item',
    fileName: 'jira-owha-sprint-export.csv',
    rows: [
      {
        'Issue key': 'OWHA-142',
        Summary: 'Integrate virtual care scheduling API with Sentinel Guard',
        Status: 'In Progress',
        'Issue Type': 'Story',
        Assignee: 'USR-PJM-TURNER',
        Updated: '2026-07-01 10:03',
      },
      {
        'Issue key': 'OWHA-151',
        Summary: 'Migrate legacy telehealth session store to cloud',
        Status: 'In Review',
        'Issue Type': 'Task',
        Assignee: 'USR-PJM-NGUYEN',
        Updated: '2026-07-02 09:41',
      },
      {
        'Issue key': 'OWHA-158',
        Summary: 'Harden tele-behavioral video encryption',
        Status: 'To Do',
        'Issue Type': 'Story',
        Assignee: 'USR-PO-CHEN',
        Updated: '2026-07-02 15:12',
      },
      {
        'Issue key': 'OWHA-163',
        Summary: '',
        Status: 'In Progress',
        'Issue Type': 'Bug',
        Assignee: 'USR-PJM-NGUYEN',
        Updated: '2026-07-03 08:27',
      },
      {
        'Issue key': 'OWHA-170',
        Summary: 'Add telehealth uptime SLA dashboard widget',
        Status: 'Done',
        'Issue Type': 'Task',
        Assignee: 'USR-PJM-TURNER',
        Updated: '2026-07-03 13:50',
      },
    ],
  },
];

// Connector profiles shown beside the distinct, real local-file ingestion path.
// IDs map to seed-data/connectors.seed.json.
const SOURCE_TRUST_CONNECTORS = [
  { id: 'CONN-PMRT-FINANCE', systemLabel: 'FMIS / Tableau (Finance)' },
  { id: 'CONN-SERVICENOW-CHANGE', systemLabel: 'ServiceNow (Requests & Change)' },
  { id: 'CONN-JIRA-AGILE', systemLabel: 'Jira (Agile Delivery)' },
];

const SYNC_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// Deterministic, locale-independent formatter for a connector's last successful
// sync timestamp (e.g. "2026-05-14T05:42:00-04:00" -> "May 14, 2026, 05:42").
function formatSyncTimestamp(value) {
  const text = typeof value === 'string' ? value.trim() : '';
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(text);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match;
  const monthLabel = SYNC_MONTHS[Number(month) - 1] || month;
  return `${monthLabel} ${Number(day)}, ${year}, ${hour}:${minute}`;
}

function formatRefreshCadence(value) {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) return null;
  return text.replace(/-mock$/i, '').replace(/-/g, ' ');
}

let fmIsWorkbookBase64Promise = null;

function escapeXml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function columnLabel(columnNumber) {
  let value = columnNumber;
  let label = '';
  while (value > 0) {
    const remainder = (value - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    value = Math.floor((value - 1) / 26);
  }
  return label;
}

async function buildWorkbookBase64({ sheetName, rows }) {
  const zip = new JSZip();
  const sharedStrings = [];
  const sharedStringIndex = new Map();

  function getSharedStringIndex(value) {
    const normalized = String(value ?? '');
    if (!sharedStringIndex.has(normalized)) {
      sharedStringIndex.set(normalized, sharedStrings.length);
      sharedStrings.push(normalized);
    }
    return sharedStringIndex.get(normalized);
  }

  const sheetRowsXml = rows
    .map((row, rowIndex) => {
      const cells = row
        .map((cellValue, columnIndex) => {
          const ref = `${columnLabel(columnIndex + 1)}${rowIndex + 1}`;
          const stringIndex = getSharedStringIndex(cellValue);
          return `<c r="${ref}" t="s"><v>${stringIndex}</v></c>`;
        })
        .join('');
      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join('');

  zip.file(
    '[Content_Types].xml',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/sharedStrings.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sharedStrings+xml"/>
</Types>`,
  );
  zip.file(
    '_rels/.rels',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`,
  );
  zip.file(
    'xl/workbook.xml',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="${escapeXml(sheetName)}" sheetId="1" r:id="rId1"/>
  </sheets>
</workbook>`,
  );
  zip.file(
    'xl/_rels/workbook.xml.rels',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings" Target="sharedStrings.xml"/>
</Relationships>`,
  );
  zip.file(
    'xl/worksheets/sheet1.xml',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheetData>${sheetRowsXml}</sheetData>
</worksheet>`,
  );
  zip.file(
    'xl/sharedStrings.xml',
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="${sharedStrings.length}" uniqueCount="${sharedStrings.length}">${sharedStrings
      .map((value) => `<si><t>${escapeXml(value)}</t></si>`)
      .join('')}</sst>`,
  );

  return zip.generateAsync({ type: 'base64' });
}

const UPLOAD_TARGET_ENTITY_TYPES = [
  { value: 'financial_record', label: 'Financial record' },
  { value: 'action', label: 'Action' },
  { value: 'work_item', label: 'Work item' },
];

// A real source-extract upload can only target a governed source-of-record
// connector (finance, ITSM, delivery, registry, …). AI / knowledge-base / RAG
// connectors (e.g. CONN-BEDROCK-KB) are retrieval endpoints, not row-ingest
// sources — offering them as an upload target (and silently defaulting to one)
// misleads the steward and breaks the connector-matched mapping template. Detect
// them by id, systemType, or data-domain so the filter holds even when the
// connector list omits the richer metadata fields.
export function isIngestCapableConnector(connector) {
  if (!connector || !connector.id) return false;
  const id = String(connector.id).toUpperCase();
  if (/BEDROCK|KNOWLEDGE|RAG/.test(id)) return false;
  const systemType = String(connector.systemType || '').toLowerCase();
  if (/rag|knowledge|bedrock/.test(systemType)) return false;
  const domains = []
    .concat(connector.dataDomains || [], connector.authoritativeFor || [])
    .map((domain) => String(domain).toLowerCase());
  // Only the AI/RAG signals ('ai', 'citations') disqualify a connector — generic
  // document connectors (SharePoint/Confluence) stay selectable as before.
  if (domains.some((domain) => domain === 'ai' || domain === 'citations')) {
    return false;
  }
  return true;
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Could not read file'));
    reader.readAsText(file);
  });
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error || new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

function starterRowsToWorksheetRows(sample) {
  const records = Array.isArray(sample.rows) ? sample.rows : [];
  if (records.length === 0) return [];
  const headers = Object.keys(records[0]);
  return [headers, ...records.map((record) => headers.map((header) => record[header] ?? ''))];
}

function getStarterWorkbookBase64(sample) {
  if (sample.id !== 'starter-fmis') return null;
  if (!fmIsWorkbookBase64Promise) {
    fmIsWorkbookBase64Promise = buildWorkbookBase64({
      sheetName: sample.sourceSheetName || 'Sheet1',
      rows: starterRowsToWorksheetRows(sample),
    });
  }
  return fmIsWorkbookBase64Promise;
}

export function IngestionCenterPage() {
  const navigate = useNavigate();
  const connectorsRes = useApi('/connectors', { query: { limit: 50 } });
  const templatesRes = useApi('/mapping-templates', { query: { limit: 50 } });
  const jobsRes = useApi('/ingestion/jobs', { query: { limit: 50 } });
  const stageMutation = useMutation();
  const uploadMutation = useMutation();
  // Real file upload (AC-DATA-3.1): parsed payload staged client-side before POST.
  const [uploadPayload, setUploadPayload] = useState(null);
  const [uploadReadError, setUploadReadError] = useState(null);
  const [uploadSourceSystem, setUploadSourceSystem] = useState('');
  const [uploadTargetEntityType, setUploadTargetEntityType] = useState('financial_record');

  usePageMetadata({
    title: 'Ingestion Center',
    subtitle: 'Upload, map, validate, and commit governed source data into DHA PRIME.',
  });

  if (connectorsRes.loading || templatesRes.loading || jobsRes.loading) {
    return (
      <>
        <PageHeader title="Ingestion Center" />
        <KpiBandSkeleton count={4} />
        <LoadingSkeleton rows={6} />
      </>
    );
  }
  if (connectorsRes.error) return <ErrorState error={connectorsRes.error} />;
  if (templatesRes.error) return <ErrorState error={templatesRes.error} />;
  if (jobsRes.error) return <ErrorState error={jobsRes.error} />;

  const connectors = connectorsRes.data?.data || [];
  // Only governed source-of-record connectors may back a real upload (excludes
  // AI/knowledge-base connectors like CONN-BEDROCK-KB).
  const ingestCapableConnectors = connectors.filter(isIngestCapableConnector);
  const templates = templatesRes.data?.data || [];
  const jobs = jobsRes.data?.data || [];

  // ----- Ingestion rollups derived from already-fetched lists -----
  const healthyConnectorCount = connectors.filter(
    (connector) => String(connector.status || 'healthy').toLowerCase() === 'healthy',
  ).length;
  const committedJobCount = jobs.filter(
    (job) => String(job.status || '').toLowerCase() === 'committed',
  ).length;
  const stagedRowTotal = jobs.reduce((sum, job) => sum + Number(job.rowCount || 0), 0);

  // Job status distribution — every staged job lands in exactly one bucket.
  const jobStatusSegments = (() => {
    const counts = new Map();
    for (const job of jobs) {
      const raw = String(job.status || 'uploaded').toLowerCase();
      const tone =
        raw === 'committed' || raw === 'ready_to_commit'
          ? 'sage'
          : raw.includes('warning')
            ? 'amber'
            : raw.includes('fail') || raw === 'error'
              ? 'brick'
              : 'blue';
      const label =
        raw === 'ready_to_commit'
          ? 'Ready to commit'
          : raw.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
      const prev = counts.get(label) || { label, count: 0, tone };
      prev.count += 1;
      counts.set(label, prev);
    }
    return [...counts.values()];
  })();

  const ingestionKpiItems = [
    {
      id: 'connectors',
      label: 'Configured connector profiles',
      value: connectors.length,
      status:
        healthyConnectorCount === connectors.length && connectors.length > 0 ? 'sage' : 'blue',
      statusLabel: connectors.length > 0 ? `${healthyConnectorCount} healthy` : 'None',
      footer: 'Prototype source domains',
    },
    {
      id: 'templates',
      label: 'Mapping templates',
      value: templates.length,
      status: templates.length > 0 ? 'blue' : 'slate',
      statusLabel: templates.length > 0 ? 'Reusable' : 'None',
      footer: 'Field-mapping reuse',
    },
    {
      id: 'jobs',
      label: 'Staged jobs',
      value: jobs.length,
      status: jobs.length > 0 ? 'blue' : 'slate',
      statusLabel: committedJobCount > 0 ? `${committedJobCount} committed` : 'In queue',
      footer: `${stagedRowTotal} row${stagedRowTotal === 1 ? '' : 's'} staged`,
    },
    {
      id: 'committed',
      label: 'Committed imports',
      value: committedJobCount,
      status: committedJobCount > 0 ? 'sage' : 'slate',
      statusLabel: committedJobCount > 0 ? 'Lineage written' : 'None yet',
      footer: `${jobs.length} job${jobs.length === 1 ? '' : 's'} tracked`,
    },
  ];

  async function handleUploadFileChange(event) {
    const file = event.target.files?.[0] || null;
    setUploadPayload(null);
    setUploadReadError(null);
    if (!file) return;
    try {
      const isXlsx = /\.xlsx$/i.test(file.name);
      if (isXlsx) {
        const workbookBase64 = await readFileAsBase64(file);
        setUploadPayload({ fileName: file.name, workbookBase64 });
      } else {
        const csvText = await readFileAsText(file);
        setUploadPayload({ fileName: file.name, csvText });
      }
    } catch (e) {
      setUploadReadError(e.message || 'Could not read the selected file.');
    }
  }

  async function handleStageUpload() {
    // A source connector must be chosen explicitly — never silently defaulted, so
    // the connector-matched mapping template resolves correctly for the file.
    if (!uploadPayload || !uploadSourceSystem) return;
    const sourceSystem = uploadSourceSystem;
    try {
      const response = await uploadMutation.run(() =>
        api.post('/ingestion/uploads', {
          fileName: uploadPayload.fileName,
          sourceSystem,
          targetEntityType: uploadTargetEntityType,
          ...(uploadPayload.workbookBase64
            ? { workbookBase64: uploadPayload.workbookBase64 }
            : { csvText: uploadPayload.csvText }),
        }),
      );
      const createdJob = response?.data || response;
      toast.success({
        title: 'Upload staged',
        body: `${uploadPayload.fileName} · ${createdJob?.rowCount ?? '—'} row(s) detected`,
      });
      if (createdJob?.id) {
        navigate(`/admin/ingestion/${createdJob.id}`);
        return;
      }
      jobsRes.refresh();
    } catch (e) {
      toast.error({ title: 'Upload failed', body: e.message });
    }
  }

  async function handleStageSample(sample) {
    try {
      const workbookBase64 = sample.fileName.toLowerCase().endsWith('.xlsx')
        ? await getStarterWorkbookBase64(sample)
        : null;
      const response = await stageMutation.run(() =>
        api.post('/ingestion/uploads', {
          fileName: sample.fileName,
          sourceSystem: sample.sourceSystem,
          targetEntityType: sample.targetEntityType,
          rows: workbookBase64 ? [] : sample.rows,
          workbookBase64,
          ingestionMode: sample.ingestionMode,
          sourceSheetName: sample.sourceSheetName || null,
        }),
      );
      const createdJob = response?.data || response;
      if (createdJob?.id) {
        navigate(`/admin/ingestion/${createdJob.id}`);
        return;
      }
      jobsRes.refresh();
    } catch (e) {
      toast.error({ title: 'Could not stage sample import', body: e.message });
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Data & Trust"
        title="Ingestion Center"
        subtitle="Stage CSV/Excel-style source extracts, preview mappings, validate quality, and commit canonical OWHA records with lineage."
      />

      <KpiHeroBand
        className="ingestion-rollups"
        eyebrow="Pipeline posture"
        asOf={jobsRes.loadedAt}
        columns={4}
        items={ingestionKpiItems}
      />

      <section className="card ingestion-source-trust-card">
        <div>
          <div className="eyebrow">Connector simulation</div>
          <h2 className="card-title">Prototype source posture</h2>
          <p className="muted">
            These connector profiles expose simulated adapter posture only; they do not prove an
            external system was contacted. CSV and Excel uploads below remain a real local ingestion
            path.
          </p>
        </div>
        <div className="ingestion-source-trust-grid" data-testid="source-trust-band">
          {SOURCE_TRUST_CONNECTORS.map((entry) => {
            const connector = connectors.find((candidate) => candidate.id === entry.id);
            if (!connector) return null;
            const lastSync = formatSyncTimestamp(connector.health?.lastSuccessAt);
            const cadence = formatRefreshCadence(connector.refreshCadence);
            return (
              <article key={entry.id} className="ingestion-source-trust-item">
                <header className="row spread">
                  <div>
                    <strong>{connector.name}</strong>
                    <small className="muted">{entry.systemLabel}</small>
                  </div>
                  <StatusChip status={connector.status || 'healthy'} />
                </header>
                <ProvenanceBadge
                  value={connector.lastConnectionTest || connector}
                  kind="connector"
                />
                <dl className="ingestion-source-trust-meta">
                  <div>
                    <dt>Recorded prototype timestamp</dt>
                    <dd title={connector.health?.lastSuccessAt || undefined}>
                      {lastSync || 'Not recorded'}
                    </dd>
                  </div>
                  <div>
                    <dt>Configured cadence</dt>
                    <dd>{cadence || 'On demand'}</dd>
                  </div>
                </dl>
              </article>
            );
          })}
        </div>
      </section>

      {jobs.length > 0 ? (
        <section className="card ingestion-distribution-grid">
          <div className="ingestion-distribution-panel">
            <StatusDonutMini
              segments={jobStatusSegments}
              totalLabel="Jobs"
              ariaLabel="Ingestion job status distribution"
            />
            <div>
              <div className="eyebrow">Import queue status</div>
              <p className="muted small">
                Every staged job by lifecycle state — from upload through validation to committed
                lineage.
              </p>
            </div>
          </div>
        </section>
      ) : null}

      <section className="card ingestion-upload-card">
        <div>
          <div className="eyebrow">Real file upload</div>
          <h2 className="card-title">Stage a CSV or Excel file from your machine</h2>
          <p className="muted">
            Upload a real source extract: the server detects columns and stages sample rows, then
            you map, validate, and commit it like any governed import.
          </p>
        </div>
        <div className="ingestion-upload-controls">
          <label className="ingestion-field">
            <span className="muted small">Source file (.csv or .xlsx)</span>
            <input
              type="file"
              accept=".csv,.xlsx"
              aria-label="Upload source file"
              onChange={handleUploadFileChange}
            />
          </label>
          <label className="ingestion-field">
            <span className="muted small">Source connector</span>
            <select
              className="ingestion-select"
              aria-label="Source connector for upload"
              value={uploadSourceSystem}
              onChange={(event) => setUploadSourceSystem(event.target.value)}
            >
              <option value="">Select source connector</option>
              {ingestCapableConnectors.map((connector) => (
                <option key={connector.id} value={connector.id}>
                  {connector.name}
                </option>
              ))}
            </select>
          </label>
          <label className="ingestion-field">
            <span className="muted small">Target entity</span>
            <select
              className="ingestion-select"
              aria-label="Target entity for upload"
              value={uploadTargetEntityType}
              onChange={(event) => setUploadTargetEntityType(event.target.value)}
            >
              {UPLOAD_TARGET_ENTITY_TYPES.map((entity) => (
                <option key={entity.value} value={entity.value}>
                  {entity.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn primary"
            disabled={!uploadPayload || !uploadSourceSystem || uploadMutation.running}
            onClick={handleStageUpload}
          >
            {uploadMutation.running ? 'Staging…' : 'Stage uploaded file'}
          </button>
        </div>
        {uploadPayload ? (
          <div className="row" style={{ gap: 6 }}>
            <span className="chip outline sm mono">{uploadPayload.fileName}</span>
            <span className="chip outline sm">
              {uploadPayload.workbookBase64 ? 'Excel workbook' : 'CSV text'}
            </span>
          </div>
        ) : null}
        {uploadReadError ? <p className="muted small">{uploadReadError}</p> : null}
      </section>

      <section className="card ingestion-overview-card">
        <div>
          <div className="eyebrow">Starter pathways</div>
          <h2 className="card-title">Enterprise ingestion demos</h2>
          <p className="muted">
            These seeded imports cover finance, support-request workflow, and delivery execution so
            the bake-off can show three distinct integration domains.
          </p>
          <div className="row gap-sm">
            <Link className="btn secondary sm" to={ENHANCEMENT_REPORT_ENTRY_PATH}>
              Draft enhancement report
            </Link>
          </div>
        </div>
        <div className="ingestion-starter-grid">
          {STARTER_IMPORTS.map((sample) => (
            <article key={sample.id} className="ingestion-starter-card">
              <header className="row spread">
                <strong>{sample.label}</strong>
                <div className="row gap-sm">
                  <span className="chip outline sm">{sample.ingestionModeLabel}</span>
                  <span className="chip outline sm">
                    {entityTypeLabel(sample.targetEntityType)}
                  </span>
                </div>
              </header>
              <p className="muted small">{sample.detail}</p>
              <div className="ingestion-starter-footer">
                <span className="ingestion-starter-file muted small mono">{sample.fileName}</span>
                <button
                  type="button"
                  className="btn primary sm"
                  disabled={stageMutation.running}
                  onClick={() => handleStageSample(sample)}
                >
                  {sample.label === 'FMIS / Tableau'
                    ? 'Stage FMIS sample'
                    : `Stage ${sample.label}`}
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bento admin-bento">
        <div className="span-5 card" data-testid="connector-posture">
          <div className="eyebrow">Connector posture</div>
          <h2 className="card-title">Approved source domains</h2>
          {connectors.length === 0 ? (
            <BrandedEmptyState
              icon="🔌"
              eyebrow="Connector posture"
              title="No connectors loaded"
              description="Approved source domains will appear here once connectors are registered."
            />
          ) : (
            <ul className="ingestion-inline-list">
              {connectors.slice(0, 8).map((connector) => (
                <li key={connector.id}>
                  <span>
                    <strong>{connector.name || sourceSystemLabel(connector.id)}</strong>
                    {/* The raw canonical id stays only as a mono hint for stewards,
                        never as the readable label. */}
                    <small className="mono" title={connector.id}>
                      {connector.id}
                    </small>
                  </span>
                  <StatusChip status={connector.status || 'healthy'} />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="span-3 card">
          <div className="eyebrow">Reusable mappings</div>
          <h2 className="card-title">Templates</h2>
          {templates.length === 0 ? (
            <BrandedEmptyState
              icon="🧭"
              eyebrow="Reusable mappings"
              title="No templates yet"
              description="Reusable field mappings will be listed here as they are authored."
            />
          ) : (
            <ul className="ingestion-inline-list compact">
              {templates.map((template) => (
                <li key={template.id}>
                  <span>
                    <strong>{template.name}</strong>
                    <small>{template.id}</small>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="span-4 card">
          <div className="eyebrow">Recent staged jobs</div>
          <h2 className="card-title">Import queue</h2>
          {jobs.length === 0 ? (
            <BrandedEmptyState
              icon="📥"
              eyebrow="Import queue"
              title="No staged jobs"
              description="Stage a starter pathway above to create your first governed import."
            />
          ) : (
            <ul className="ingestion-job-list">
              {jobs.map((job) => (
                <li key={job.id}>
                  <div>
                    <Link className="ingestion-job-link" to={`/admin/ingestion/${job.id}`}>
                      {job.id}
                    </Link>
                    <div className="muted small">
                      {sourceSystemLabel(job.sourceSystem) || job.sourceSystem} ·{' '}
                      {entityTypeLabel(job.targetEntityType)} · {job.rowCount || 0} rows
                    </div>
                  </div>
                  <StatusChip status={job.status || 'uploaded'} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </>
  );
}

// Preferred EXECUTION mapping-template ids per source domain. When a freshly
// staged job's connector + target entity matches more than one template (e.g.
// the execution template AND a validation/stress rehearsal variant that shares
// the same connector/target), the job-detail default must land on the execution
// template — never the *-VALIDATION-* / *-STRESS-* variant.
const PREFERRED_EXECUTION_TEMPLATE_IDS = [
  'MAP-FMIS-FINANCIALS-V1',
  'MAP-SNOW-RFS-ACTIONS-V1',
  'MAP-JIRA-WORKITEMS-V1',
];

function isValidationOrStressTemplate(template) {
  const haystack = `${template?.id || ''} ${template?.name || ''}`.toLowerCase();
  return /validation|stress/.test(haystack);
}

// Choose the mapping template a job should default to. Among templates matching
// the job's connector + target entity, prefer (1) an explicit preferred
// execution id, then (2) any template that is NOT a validation/stress variant,
// then (3) any connector+target match. Fall back to a connector-only match, then
// the first template overall.
export function pickDefaultTemplateId(templates, job) {
  if (!Array.isArray(templates) || templates.length === 0 || !job) return '';
  const exactMatches = templates.filter(
    (template) =>
      template.sourceSystem === job.sourceSystem &&
      template.targetEntityType === job.targetEntityType,
  );
  const preferred =
    exactMatches.find((template) => PREFERRED_EXECUTION_TEMPLATE_IDS.includes(template.id)) ||
    exactMatches.find((template) => !isValidationOrStressTemplate(template)) ||
    exactMatches[0];
  if (preferred) return preferred.id;
  const connectorMatch = templates.find((template) => template.sourceSystem === job.sourceSystem);
  return (connectorMatch || templates[0]).id;
}

export function IngestionJobDetailPage() {
  const { jobId } = useParams();
  const jobRes = useApi(`/ingestion/jobs/${jobId}`);
  const errorsRes = useApi(`/ingestion/jobs/${jobId}/errors`);
  const templatesRes = useApi('/mapping-templates', {
    query: {
      sourceSystem: jobRes.data?.data?.sourceSystem,
      targetEntityType: jobRes.data?.data?.targetEntityType,
      limit: 50,
    },
    skip: !jobRes.data?.data,
  });
  const mapMutation = useMutation();
  const validateMutation = useMutation();
  const commitMutation = useMutation();
  const dispositionMutation = useMutation();
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  // Interactive column remap (AC-DATA-3.1): user overrides per source column.
  const [customTargets, setCustomTargets] = useState({});
  // Fix-disposition mini form (AC-DATA-4.1): one open draft at a time.
  const [fixDraft, setFixDraft] = useState(null);

  usePageMetadata({
    title: 'Ingestion Job Detail',
    subtitle: 'Validation posture, errors, duplicates, and commit readiness for a staged import.',
  });

  const job = jobRes.data?.data || null;
  const errors = errorsRes.data?.data || EMPTY_ITEMS;
  const templates = templatesRes.data?.data || EMPTY_ITEMS;
  const committedProjectFinanceTargets = getCommittedProjectFinanceTargets(job);
  const primaryCommittedProjectFinanceTarget = committedProjectFinanceTargets[0] || null;
  const secondaryCommittedProjectFinanceTargets = committedProjectFinanceTargets.slice(1);
  // S1 — the "watch it land" enrichment diffs + the deep-link target for the
  // focused provenance graph (the first canonical record written this commit).
  const enrichmentDiffs = Array.isArray(job?.enrichmentDiffs) ? job.enrichmentDiffs : EMPTY_ITEMS;
  const firstCreatedEntityId = job?.createdEntityIds?.[0] || null;
  const focusedLineageHref = firstCreatedEntityId
    ? `/admin/lineage?targetId=${encodeURIComponent(firstCreatedEntityId)}`
    : '/admin/lineage';
  const selectedTemplate =
    templates.find((template) => template.id === (job?.mappingTemplateId || selectedTemplateId)) ||
    null;

  useEffect(() => {
    if (!job) return;
    if (!job.mappingTemplateId && !selectedTemplateId && templates.length > 0) {
      // Default to the EXECUTION template matching this job's connector AND target
      // entity (e.g. an FMIS financial job defaults to MAP-FMIS-FINANCIALS-V1),
      // never a validation/stress variant that shares the same connector/target
      // and merely happens to sort first in the list.
      setSelectedTemplateId(pickDefaultTemplateId(templates, job));
      return;
    }
    if (job.mappingTemplateId && selectedTemplateId !== job.mappingTemplateId) {
      setSelectedTemplateId(job.mappingTemplateId);
    }
  }, [job, selectedTemplateId, templates]);

  const mappedRows = Array.isArray(job?.mappedRows) ? job.mappedRows : EMPTY_ITEMS;
  const rawRows = Array.isArray(job?.rawRows) ? job.rawRows : EMPTY_ITEMS;
  const previewRows = mappedRows.length > 0 ? mappedRows : rawRows;
  const previewColumns = previewRows[0]
    ? Object.keys(previewRows[0]).filter((key) => !key.startsWith('_'))
    : EMPTY_ITEMS;
  const validationSummary = job?.validation?.summary || {
    totalRows: job?.rowCount || 0,
    validRowCount: job?.validRowCount || 0,
    errorRowCount: job?.errorRowCount || 0,
    duplicateCount: job?.duplicateCount || 0,
    conflictCount: job?.conflictCount || 0,
    warningRowCount: 0,
  };
  const referenceCoverage = job?.validation?.referenceCoverage || null;
  const findings = useMemo(() => {
    const derived = [];
    const rowResults = Array.isArray(job?.validation?.rowResults)
      ? job.validation.rowResults
      : EMPTY_ITEMS;
    rowResults.forEach((rowResult) => {
      (rowResult.errors || EMPTY_ITEMS).forEach((entry) => {
        derived.push({ ...entry, rowNumber: entry.rowNumber || rowResult.rowNumber });
      });
      (rowResult.warnings || EMPTY_ITEMS).forEach((entry) => {
        derived.push({ ...entry, rowNumber: entry.rowNumber || rowResult.rowNumber });
      });
    });
    if (derived.length > 0) return derived;
    return errors;
  }, [errors, job]);
  const groupedFindings = useMemo(() => {
    const enrichedFindings = findings.map((finding) => ({
      ...finding,
      mappedContext: buildRowContext(mappedRows[finding.rowNumber - 1], finding.field),
      sourceContext: buildRowContext(
        rawRows[finding.rowNumber - 1],
        finding.sourceColumn || finding.field,
      ),
    }));
    return FINDING_GROUPS.map((group) => ({
      ...group,
      items: enrichedFindings.filter(group.match),
    }));
  }, [findings, mappedRows, rawRows]);

  const detectedColumns =
    Array.isArray(job?.detectedColumns) && job.detectedColumns.length > 0
      ? job.detectedColumns
      : rawRows[0]
        ? Object.keys(rawRows[0]).filter((key) => !key.startsWith('_'))
        : EMPTY_ITEMS;

  // Suggested canonical fields for the remap editor, drawn from the mapping
  // templates that match this job's source system and target entity type.
  const knownTargetFields = useMemo(() => {
    const fields = new Set();
    templates.forEach((template) => {
      (template.fieldMappings || EMPTY_ITEMS).forEach((mapping) => {
        if (mapping.targetField) fields.add(mapping.targetField);
      });
    });
    (job?.fieldMappings || EMPTY_ITEMS).forEach((mapping) => {
      if (mapping.targetField) fields.add(mapping.targetField);
    });
    return Array.from(fields).sort((left, right) =>
      fieldLabel(left).localeCompare(fieldLabel(right), undefined, { sensitivity: 'base' }),
    );
  }, [templates, job]);

  function defaultTargetForColumn(column) {
    const applied = Array.isArray(job?.fieldMappings)
      ? job.fieldMappings.find((mapping) => mapping.sourceColumn === column)
      : null;
    if (applied?.targetField) return applied.targetField;
    const templateMapping = (selectedTemplate?.fieldMappings || EMPTY_ITEMS).find(
      (mapping) => mapping.sourceColumn === column,
    );
    return templateMapping?.targetField || '';
  }

  function effectiveTargetForColumn(column) {
    return customTargets[column] !== undefined
      ? customTargets[column]
      : defaultTargetForColumn(column);
  }

  if (jobRes.loading || errorsRes.loading || templatesRes.loading) {
    return (
      <>
        <PageHeader title="Ingestion Job Detail" />
        <KpiBandSkeleton count={4} />
        <LoadingSkeleton rows={5} />
      </>
    );
  }
  if (jobRes.error) return <ErrorState error={jobRes.error} />;
  if (errorsRes.error) return <ErrorState error={errorsRes.error} />;
  if (templatesRes.error) return <ErrorState error={templatesRes.error} />;

  if (!job) return <EmptyState title="Ingestion job not found" />;

  function setJobEnvelope(nextJob) {
    jobRes.setData((current) =>
      current
        ? {
            ...current,
            data: nextJob,
          }
        : current,
    );
  }

  function syncErrorEnvelope(nextJob) {
    const nextErrors = Array.isArray(nextJob?.validation?.errors) ? nextJob.validation.errors : [];
    errorsRes.setData((current) => ({
      ...(current || {}),
      data: nextErrors,
      page: {
        limit: nextErrors.length || 1,
        offset: 0,
        total: nextErrors.length,
      },
    }));
  }

  function absorbJobResponse(response) {
    const nextJob = response?.data || response;
    if (nextJob?.id) {
      setJobEnvelope(nextJob);
      syncErrorEnvelope(nextJob);
    }
    return nextJob;
  }

  async function applyMappingTemplate() {
    try {
      const response = await mapMutation.run(() =>
        api.post(`/ingestion/jobs/${jobId}/map`, {
          mappingTemplateId: selectedTemplateId,
        }),
      );
      absorbJobResponse(response);
      setCustomTargets({});
      toast.success({
        title: 'Mapping template applied',
        body: selectedTemplate?.name || selectedTemplateId,
      });
    } catch (e) {
      toast.error({ title: 'Mapping failed', body: e.message });
    }
  }

  async function applyCustomMapping() {
    const fieldMappings = detectedColumns
      .map((column) => ({
        sourceColumn: column,
        targetField: String(effectiveTargetForColumn(column) || '').trim(),
      }))
      .filter((mapping) => mapping.targetField);
    try {
      const response = await mapMutation.run(() =>
        api.post(`/ingestion/jobs/${jobId}/map`, { fieldMappings }),
      );
      absorbJobResponse(response);
      toast.success({
        title: 'Custom mapping applied',
        body: `${fieldMappings.length} column(s) mapped to canonical fields`,
      });
    } catch (e) {
      toast.error({ title: 'Mapping failed', body: e.message });
    }
  }

  async function validateJob() {
    try {
      const response = await validateMutation.run(() =>
        api.post(`/ingestion/jobs/${jobId}/validate`, {}),
      );
      const nextJob = absorbJobResponse(response);
      toast.success({
        title: 'Validation complete',
        body: `${nextJob?.validation?.summary?.errorRowCount ?? 0} error row(s) · ${nextJob?.validation?.summary?.warningRowCount ?? 0} warning row(s)`,
      });
    } catch (e) {
      toast.error({ title: 'Validation failed', body: e.message });
    }
  }

  async function commitJob() {
    try {
      const response = await commitMutation.run(() =>
        api.post(`/ingestion/jobs/${jobId}/commit`, {}),
      );
      const nextJob = absorbJobResponse(response);
      toast.success({
        title: 'Import committed',
        body: `${nextJob?.commitSummary?.createdRecordCount ?? 0} record(s) created with lineage`,
      });
    } catch (e) {
      toast.error({ title: 'Commit failed', body: e.message });
    }
  }

  async function applyDisposition(body) {
    try {
      const response = await dispositionMutation.run(() =>
        api.post(`/ingestion/jobs/${jobId}/dispositions`, body),
      );
      absorbJobResponse(response);
      setFixDraft(null);
      toast.success({
        title: 'Disposition applied',
        body: `Row ${body.rowNumber} · ${humanizeEnum(body.disposition)}`,
      });
    } catch (e) {
      toast.error({ title: 'Disposition failed', body: e.message });
    }
  }

  // AC-DATA-4.1 — the commit button explains its disabled reasons inline.
  const commitBlockers = [];
  if (job.status === 'committed') {
    commitBlockers.push('This import has already been committed.');
  } else {
    if (mappedRows.length === 0) {
      commitBlockers.push('Apply a mapping template or custom column mapping first.');
    }
    if (!job.validation) {
      commitBlockers.push('Run validation before committing.');
    } else if ((validationSummary.errorRowCount || 0) > 0) {
      commitBlockers.push(
        `${validationSummary.errorRowCount} error row(s) remain — skip or fix each one to unblock commit.`,
      );
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Data & Trust"
        title="Ingestion Job Detail"
        subtitle={`${job.id} · ${sourceSystemLabel(job.sourceSystem) || job.sourceSystem} · ${entityTypeLabel(job.targetEntityType)}`}
      />

      <section className="card ingestion-stage-ribbon-card">
        <div className="eyebrow">Ingestion pipeline</div>
        <div data-testid="ingestion-stage-ribbon">
          <StagePipeline stages={deriveIngestionStages(job)} />
        </div>
      </section>

      <section className="bento admin-bento">
        <div className="span-4 card ingestion-summary-card">
          <header className="row spread">
            <div>
              <div className="eyebrow">Job status</div>
              <h2 className="card-title">{job.id}</h2>
            </div>
            <StatusChip status={job.status || 'uploaded'} />
          </header>
          <dl className="ingestion-summary-grid">
            <div>
              <dt>Source file</dt>
              <dd className="ingestion-summary-meta">{job.fileName || '—'}</dd>
            </div>
            <div>
              <dt>Ingestion mode</dt>
              <dd className="ingestion-summary-meta">
                {formatIngestionModeLabel(job.ingestionType)}
              </dd>
            </div>
            <div>
              <dt>Total rows</dt>
              <dd>{job.rowCount || 0}</dd>
            </div>
            <div>
              <dt>Valid rows</dt>
              <dd>{job.validRowCount || 0}</dd>
            </div>
            <div>
              <dt>Error rows</dt>
              <dd>{job.errorRowCount || 0}</dd>
            </div>
            <div>
              <dt>Duplicates</dt>
              <dd>{validationSummary.duplicateCount || 0}</dd>
            </div>
            <div>
              <dt>Conflicts</dt>
              <dd>{validationSummary.conflictCount || 0}</dd>
            </div>
            <div>
              <dt>Warnings</dt>
              <dd>{validationSummary.warningRowCount || 0}</dd>
            </div>
          </dl>
          {referenceCoverage ? (
            // IMP-003: same tile treatment as the counts above so the block
            // reads as a first-class validation result, not fine print.
            <div className="ingestion-reference-block" data-testid="ingestion-reference-coverage">
              <div className="eyebrow">Reference validation</div>
              <dl className="ingestion-summary-grid">
                <div>
                  <dt>Ref checked</dt>
                  <dd>{referenceCoverage.checked || 0}</dd>
                </div>
                <div>
                  <dt>Present</dt>
                  <dd>{referenceCoverage.present || 0}</dd>
                </div>
                <div>
                  <dt>Missing</dt>
                  <dd>{referenceCoverage.missing || 0}</dd>
                </div>
                <div>
                  <dt>Unchecked</dt>
                  <dd>{referenceCoverage.unchecked || 0}</dd>
                </div>
              </dl>
            </div>
          ) : null}
          {(() => {
            const totalRows = Number(validationSummary.totalRows || job.rowCount || 0);
            const validRows = Number(validationSummary.validRowCount ?? job.validRowCount ?? 0);
            const errorRows = Number(validationSummary.errorRowCount ?? job.errorRowCount ?? 0);
            const passRate = totalRows > 0 ? Math.round((validRows / totalRows) * 100) : 0;
            const errorRate = totalRows > 0 ? Math.round((errorRows / totalRows) * 100) : 0;
            if (totalRows === 0) return null;
            return (
              <div className="ingestion-quality-grid" aria-label="Validation quality">
                <div className="ingestion-quality-tile">
                  <span className="ingestion-quality-caption">Valid-row pass rate</span>
                  <InlineScoreCell
                    value={passRate}
                    max={100}
                    suffix="%"
                    tone={bandTone(passRate, { good: 90, warn: 70 })}
                    ariaLabel={`Valid-row pass rate ${passRate}%`}
                  />
                </div>
                <div className="ingestion-quality-tile">
                  <span className="ingestion-quality-caption">Error-row share</span>
                  <InlineScoreCell
                    value={errorRate}
                    max={100}
                    suffix="%"
                    tone={bandTone(errorRate, { good: 5, warn: 20, invert: true })}
                    ariaLabel={`Error-row share ${errorRate}%`}
                  />
                </div>
              </div>
            );
          })()}
          <div className="ingestion-action-row">
            <button
              type="button"
              className="btn ghost"
              disabled={validateMutation.running || mappedRows.length === 0}
              onClick={validateJob}
            >
              Validate job
            </button>
            <button
              type="button"
              className="btn primary"
              disabled={
                commitMutation.running ||
                !job.validation ||
                (validationSummary.errorRowCount || 0) > 0 ||
                job.status === 'committed'
              }
              onClick={commitJob}
            >
              Commit import
            </button>
          </div>
          {commitBlockers.length > 0 ? (
            <ul className="ingestion-commit-blockers" data-testid="commit-blockers">
              {commitBlockers.map((reason) => (
                <li key={reason} className="muted small">
                  {reason}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="span-8 card">
          <div className="eyebrow">Mapping and validation</div>
          <h2 className="card-title">Template, preview, and finding review</h2>

          <div className="ingestion-template-row">
            <label className="ingestion-field">
              <span className="muted small">Mapping template</span>
              <select
                className="ingestion-select"
                value={selectedTemplateId}
                onChange={(event) => setSelectedTemplateId(event.target.value)}
              >
                <option value="">Select a template</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn secondary"
              disabled={mapMutation.running || !selectedTemplateId}
              onClick={applyMappingTemplate}
            >
              Apply mapping template
            </button>
          </div>

          {Array.isArray(selectedTemplate?.fieldMappings) &&
          selectedTemplate.fieldMappings.length > 0 ? (
            <div className="ingestion-context-block">
              <div className="eyebrow">{`Selected mapping · ${selectedTemplate.name}`}</div>
              <div className="ingestion-preview-table-wrap">
                <table
                  className="table ingestion-selected-mapping-table"
                  data-testid="ingestion-selected-mapping"
                >
                  <thead>
                    <tr>
                      <th>Source column</th>
                      <th>Target field</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTemplate.fieldMappings.map((mapping) => (
                      <tr
                        key={`${selectedTemplate.id}-${mapping.sourceColumn}-${mapping.targetField}`}
                      >
                        <td className="mono">{mapping.sourceColumn}</td>
                        <td>
                          <div className="ingestion-mapping-target">
                            <strong>{fieldLabel(mapping.targetField)}</strong>
                            <span className="ingestion-context-hint mono">
                              {mapping.targetField}
                            </span>
                            {mapping.required ? (
                              <span className="chip outline sm">Required</span>
                            ) : null}
                            {mapping.transform ? (
                              <span className="ingestion-context-hint">
                                {`transform: ${mapping.transform}`}
                              </span>
                            ) : null}
                            {mapping.defaultValue != null && mapping.defaultValue !== '' ? (
                              <span className="ingestion-context-hint">
                                {`default: ${mapping.defaultValue}`}
                              </span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {detectedColumns.length > 0 && job.status !== 'committed' ? (
            <div className="ingestion-remap-editor">
              <div className="eyebrow">Interactive column mapping</div>
              <p className="muted small">
                Choose a known target field or type another canonical field. Select Ignore this
                column (or clear the value) to leave a source column unmapped, then apply the custom
                mapping.
              </p>
              <div className="ingestion-preview-table-wrap">
                <table className="table ingestion-remap-table">
                  <thead>
                    <tr>
                      <th>Source column</th>
                      <th>Sample value</th>
                      <th>Target field</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detectedColumns.map((column) => (
                      <tr key={column}>
                        <td className="mono">{column}</td>
                        <td>{formatContextValue(rawRows[0]?.[column])}</td>
                        <td>
                          <EditableTargetFieldCombobox
                            column={column}
                            value={effectiveTargetForColumn(column)}
                            options={knownTargetFields}
                            onChange={(nextValue) =>
                              setCustomTargets((current) => ({
                                ...current,
                                [column]: nextValue,
                              }))
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                className="btn secondary"
                disabled={
                  mapMutation.running ||
                  !detectedColumns.some((column) =>
                    String(effectiveTargetForColumn(column) || '').trim(),
                  )
                }
                onClick={applyCustomMapping}
              >
                Apply custom mapping
              </button>
            </div>
          ) : null}

          {previewRows.length > 0 ? (
            <div className="ingestion-preview-shell">
              <div className="row spread">
                <div>
                  <div className="eyebrow">
                    {mappedRows.length > 0 ? 'Mapped preview' : 'Source preview'}
                  </div>
                  <strong>
                    {previewRows.length} staged row{previewRows.length === 1 ? '' : 's'}
                  </strong>
                </div>
                {job.mappingTemplateId ? (
                  <span className="chip outline sm">{job.mappingTemplateId}</span>
                ) : (
                  <span className="chip outline sm">No template applied yet</span>
                )}
              </div>
              <div className="ingestion-preview-table-wrap">
                <table className="table ingestion-preview-table">
                  <thead>
                    <tr>
                      {previewColumns.map((column) => (
                        <th key={column}>
                          {mappedRows.length > 0 ? (
                            <>
                              <span className="ingestion-th-label">{fieldLabel(column)}</span>
                              <small className="ingestion-th-hint mono muted">{column}</small>
                            </>
                          ) : (
                            column
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.slice(0, 5).map((row, index) => (
                      <tr key={`${job.id}-preview-${index}`}>
                        {previewColumns.map((column) => (
                          <td key={column}>{String(row[column] ?? '—')}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <div className="ingestion-findings-shell">
            <div className="eyebrow">Validation findings</div>
            <h3 className="card-title">Preview blockers and data-quality notes</h3>
          </div>
          {findings.length === 0 ? (
            <BrandedEmptyState
              icon={mappedRows.length > 0 ? '✅' : '🧭'}
              eyebrow="Validation findings"
              tone={mappedRows.length > 0 ? 'sage' : 'slate'}
              title="No findings yet"
              description={
                mappedRows.length > 0
                  ? 'This staged job is ready for review and commit.'
                  : 'Apply a mapping template to preview canonical fields before validation.'
              }
            />
          ) : (
            <div className="ingestion-triage-grid">
              {groupedFindings.map((group) => (
                <section key={group.id} className="ingestion-triage-section">
                  <header className="row spread">
                    <div>
                      <h4 className="card-title">{group.title}</h4>
                      <p className="muted small">{group.description}</p>
                    </div>
                    <span className="chip outline sm">{group.items.length}</span>
                  </header>
                  {group.items.length === 0 ? (
                    <p className="muted small">
                      No rows are currently flagged in this review bucket.
                    </p>
                  ) : (
                    <ul className="ingestion-error-list ingestion-triage-list">
                      {group.items.map((finding, index) => (
                        <li
                          key={`${group.id}-${finding.rowNumber}-${finding.field || finding.code || 'row'}-${index}`}
                          className={
                            finding.severity === 'warning'
                              ? 'ingestion-finding warning'
                              : 'ingestion-finding error'
                          }
                        >
                          <div className="ingestion-finding-header">
                            <strong>Row {finding.rowNumber}</strong>
                            <small
                              title={`${finding.field || finding.code || 'general'} · ${finding.severity || 'error'}`}
                            >
                              {finding.field
                                ? fieldLabel(finding.field)
                                : humanizeEnum(finding.code || 'general', 'General')}{' '}
                              · {humanizeEnum(finding.severity || 'error')}
                            </small>
                          </div>
                          <span>{finding.message}</span>
                          {finding.mappedContext.length > 0 ? (
                            <div className="ingestion-context-block">
                              <div className="eyebrow">Mapped row context</div>
                              <ul className="ingestion-context-list">
                                {finding.mappedContext.map((entry) => (
                                  <li key={`${group.id}-${finding.rowNumber}-mapped-${entry.key}`}>
                                    <span className="muted">{fieldLabel(entry.key)}:</span>{' '}
                                    <strong>{entry.value}</strong>
                                    {fieldLabel(entry.key) !== entry.key ? (
                                      <small className="mono muted ingestion-context-hint">
                                        {entry.key}
                                      </small>
                                    ) : null}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                          {finding.sourceContext.length > 0 ? (
                            <div className="ingestion-context-block">
                              <div className="eyebrow">Source row context</div>
                              <ul className="ingestion-context-list">
                                {finding.sourceContext.map((entry) => (
                                  <li key={`${group.id}-${finding.rowNumber}-source-${entry.key}`}>
                                    <span className="muted">{entry.key}:</span>
                                    <strong>{entry.value}</strong>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                          {finding.rowNumber && job.status !== 'committed' ? (
                            <div className="row ingestion-disposition-row" style={{ gap: 6 }}>
                              <button
                                type="button"
                                className="btn ghost sm"
                                disabled={dispositionMutation.running}
                                onClick={() =>
                                  applyDisposition({
                                    rowNumber: finding.rowNumber,
                                    disposition: 'skip',
                                  })
                                }
                              >
                                Skip row {finding.rowNumber}
                              </button>
                              {finding.field ? (
                                <button
                                  type="button"
                                  className="btn ghost sm"
                                  disabled={dispositionMutation.running}
                                  onClick={() =>
                                    setFixDraft({
                                      rowNumber: finding.rowNumber,
                                      field: finding.field,
                                      value: String(
                                        mappedRows[finding.rowNumber - 1]?.[finding.field] ?? '',
                                      ),
                                    })
                                  }
                                >
                                  Fix {fieldLabel(finding.field)} for row {finding.rowNumber}
                                </button>
                              ) : null}
                              {finding.severity === 'warning' && finding.code ? (
                                <button
                                  type="button"
                                  className="btn ghost sm"
                                  disabled={dispositionMutation.running}
                                  onClick={() =>
                                    applyDisposition({
                                      rowNumber: finding.rowNumber,
                                      disposition: 'accept',
                                      code: finding.code,
                                    })
                                  }
                                >
                                  Accept row {finding.rowNumber} finding
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                          {fixDraft &&
                          fixDraft.rowNumber === finding.rowNumber &&
                          fixDraft.field === finding.field ? (
                            <div className="row ingestion-fix-row" style={{ gap: 6 }}>
                              <input
                                className="ingestion-remap-input"
                                aria-label={`Corrected value for row ${finding.rowNumber} ${finding.field}`}
                                value={fixDraft.value}
                                onChange={(event) =>
                                  setFixDraft((current) => ({
                                    ...current,
                                    value: event.target.value,
                                  }))
                                }
                              />
                              <button
                                type="button"
                                className="btn secondary sm"
                                disabled={dispositionMutation.running}
                                onClick={() =>
                                  applyDisposition({
                                    rowNumber: fixDraft.rowNumber,
                                    disposition: 'fix',
                                    field: fixDraft.field,
                                    fixValue: fixDraft.value,
                                  })
                                }
                              >
                                Save {fieldLabel(fixDraft.field)} fix for row {finding.rowNumber}
                              </button>
                              <button
                                type="button"
                                className="btn ghost sm"
                                onClick={() => setFixDraft(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>
          )}
        </div>
      </section>

      {job.status === 'committed' || job.commitSummary ? (
        <section className="card ingestion-results-card">
          <div className="row spread">
            <div>
              <div className="eyebrow">Commit outcome</div>
              <h2 className="card-title">Lineage and canonical record results</h2>
            </div>
            <div className="ingestion-action-row">
              {primaryCommittedProjectFinanceTarget ? (
                <Link className="btn secondary sm" to={primaryCommittedProjectFinanceTarget.href}>
                  Open updated project finance
                </Link>
              ) : null}
              <Link className="btn primary sm" to={focusedLineageHref}>
                See where this came from
              </Link>
            </div>
          </div>

          {enrichmentDiffs.length > 0 ? (
            <div className="ingestion-watch-land" data-testid="watch-it-land">
              <div className="eyebrow">Watch it land</div>
              <p className="muted small">
                Every enriched canonical value, before and after this commit — sourced, never
                invented. Aggregated per project across the committed rows.
              </p>
              <div className="ingestion-preview-table-wrap">
                <table className="table ingestion-diff-table">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Project</th>
                      <th>Source system</th>
                      <th>Before</th>
                      <th>After</th>
                      <th>Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrichmentDiffs.map((diff) => {
                      const deltaLabel = formatDiffDelta(diff.delta);
                      return (
                        <tr key={`${diff.scopeId}-${diff.field}`}>
                          <td>
                            <strong>{diffFieldLabel(diff.field)}</strong>
                            <small className="muted mono"> {diff.field}</small>
                          </td>
                          <td className="mono">{diff.scopeId}</td>
                          <td>{sourceSystemLabel(diff.sourceSystem) || '—'}</td>
                          <td>
                            {diff.isNew ? (
                              <span
                                className="ingestion-diff-new"
                                title="No prior canonical record for this field"
                              >
                                — (new)
                              </span>
                            ) : (
                              formatDiffValue(diff.before)
                            )}
                            {!diff.isNew && diff.beforeSource ? (
                              <small className="muted"> · {diff.beforeSource}</small>
                            ) : null}
                          </td>
                          <td>
                            <strong>{formatDiffValue(diff.after)}</strong>
                          </td>
                          <td>
                            {deltaLabel ? (
                              <span className={`ingestion-diff-delta ${diffDeltaTone(diff.delta)}`}>
                                {deltaLabel}
                              </span>
                            ) : (
                              <span className="ingestion-diff-new">new</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
          <div className="ingestion-summary-grid">
            <div>
              <dt>Created records</dt>
              <dd>{job.commitSummary?.createdRecordCount || job.createdEntityIds?.length || 0}</dd>
            </div>
            <div>
              <dt>Lineage records created</dt>
              <dd>{job.commitSummary?.lineageRecordCount || job.lineageRecordIds?.length || 0}</dd>
            </div>
            <div>
              <dt>Data-quality findings</dt>
              <dd>
                {job.commitSummary?.dataQualityFindingCount ||
                  job.dataQualityFindingIds?.length ||
                  0}
              </dd>
            </div>
          </div>
          {primaryCommittedProjectFinanceTarget ? (
            <div className="ingestion-commit-followthrough">
              <div className="eyebrow">Updated project trust review</div>
              <p className="muted">
                Open the affected project finance surface to confirm governed source notes,
                freshness, and trust posture after import commit.
              </p>
              <strong>{primaryCommittedProjectFinanceTarget.projectId}</strong>
              {secondaryCommittedProjectFinanceTargets.length ? (
                <ul className="ingestion-inline-list compact">
                  {secondaryCommittedProjectFinanceTargets.map((target) => (
                    <li key={target.projectId}>
                      <Link className="btn ghost sm" to={target.href}>
                        Open {target.projectId}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          <div className="ingestion-results-grid">
            <div>
              <div className="eyebrow">Created record IDs</div>
              {job.createdEntityIds?.length ? (
                <ul className="ingestion-inline-list compact">
                  {job.createdEntityIds.map((entityId) => (
                    <li key={entityId}>
                      <span>
                        <strong>{entityId}</strong>
                        <small>{entityTypeLabel(job.targetEntityType)}</small>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState title="No created records listed" />
              )}
            </div>
            <div>
              <div className="eyebrow">Source record references</div>
              {job.sourceRecordReferences?.length ? (
                <ul className="ingestion-inline-list compact">
                  {job.sourceRecordReferences.map((reference) => (
                    <li
                      key={`${reference.sourceRecordId || 'source'}-${reference.targetEntityId || 'target'}`}
                    >
                      <span>
                        <strong>{reference.sourceRecordId}</strong>
                        <small>
                          {reference.targetEntityId || entityTypeLabel(reference.targetEntityType)}
                        </small>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState title="No source record references listed" />
              )}
            </div>
            <div>
              <div className="eyebrow">Lineage record IDs</div>
              {job.lineageRecordIds?.length ? (
                <ul className="ingestion-inline-list compact">
                  {job.lineageRecordIds.map((lineageId) => (
                    <li key={lineageId}>
                      <span>
                        <strong>{lineageId}</strong>
                        <small>Lineage edge created from staged import</small>
                      </span>
                      <Link className="btn ghost sm" to="/admin/lineage">
                        View lineage
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState title="No lineage edges listed" />
              )}
            </div>
            <div>
              <div className="eyebrow">Data-quality finding IDs</div>
              {job.dataQualityFindingIds?.length ? (
                <ul className="ingestion-inline-list compact">
                  {job.dataQualityFindingIds.map((findingId) => (
                    <li key={findingId}>
                      <span>
                        <strong>{findingId}</strong>
                        <small>Data-quality finding generated from commit warnings</small>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState title="No data-quality findings listed" />
              )}
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
}
